import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ph } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useNavigate } from "react-router";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Text } from "@medoma/components/Text/Text";
import { Link } from "@medoma/components/Link/Link";
import { IconButton } from "@medoma/components/Button/Button";
import FolderIcon from "@medoma/components/icons/FolderIcon";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import NoResults from "@/components/NoResults/NoResults";
import { categoriesQueryOptions } from "@/api/Equipment";
import { deducedError } from "@/Utils/ErrorUtils";
import {
  getAssetStatusLabel,
  operationalStatusSchema,
} from "@medoma/models/equipment";
import styles from "./CategoriesList.module.scss";

export const CategoriesList = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const {
    data: categories,
    isPending,
    isError,
    error,
  } = useQuery(categoriesQueryOptions());

  if (isPending) {
    return <Loading message={t`Hämtar utrustningskategorier`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta kategorier.`} ${deducedError(error)}`}
      />
    );
  }

  if (categories.length === 0) {
    return (
      <div className={styles.noResults}>
        <NoResults message={t`Inga kategorier ännu.`} size="small" />
      </div>
    );
  }

  return (
    <div className={styles.categoriesList}>
      {categories.map((category) => (
        <section key={category.id} className={styles.category}>
          <div className={styles.categoryHeader}>
            <FolderIcon />
            <Link to={`categories/${category.id}`} size="small" color="neutral">
              {category.name}
            </Link>
            <IconButton
              aria-label={t`Lägg till utrustning i ${ph({ categoryName: category.name })}`}
              size="small"
              onClick={() => navigate(`assets/new?categoryId=${category.id}`)}
            >
              <PlusIcon />
            </IconButton>
          </div>
          {category.assets.length === 0 ? (
            <div className={styles.emptyAssets}>
              <NoResults
                message={t`Ingen utrustning av denna typ ännu.`}
                size="small"
              />
            </div>
          ) : (
            <ul className={styles.assets}>
              {category.assets.map((asset) => (
                <li
                  key={asset.id}
                  className={clsx(
                    styles.assetItemContent,
                    asset.operationalStatus ===
                      operationalStatusSchema.enum.inactive && styles.inactive,
                  )}
                >
                  <Link
                    to={`assets/${asset.id}`}
                    size="small"
                    color="neutral"
                    weight="regular"
                  >
                    {asset.comment
                      ? `${asset.name} (${asset.comment})`
                      : asset.name}
                  </Link>
                  <Text
                    element="span"
                    color="faded"
                    size="small"
                    className={styles.assetStatus}
                  >
                    {t(getAssetStatusLabel(asset))}
                  </Text>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
};
